'use client';

import { Locale, usePathname, useRouter } from '@/i18n/routing';
import { useLocale } from 'next-intl';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useTransition } from 'react';
import styles from './index.module.scss';

type Props = {
  children: Array<{ key: string; value: string; label: string }>;
  defaultValue: string;
  label: string;
  align?: 'center' | 'start' | 'end';
  showLanguage?: boolean;
};

export default function LocaleSwitcherSelect({
  children,
  defaultValue,
  label,
  align = 'center',
  showLanguage,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const [isPending, startTransition] = useTransition();

  function onLocaleChange(nextLocale: Locale) {
    startTransition(() => {
      const newPathname = pathname.startsWith(`/${currentLocale}`)
        ? pathname.replace(`/${currentLocale}`, `/${nextLocale}`)
        : `/${nextLocale}${pathname}`;

      router.replace(newPathname);
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={styles.button} disabled={isPending}>
          {showLanguage ? `Language: ` : ''}
          {children.find((item) => item.value === defaultValue)?.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side="bottom">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {children.map((cur) => (
          <DropdownMenuItem
            key={cur.key}
            onClick={() => onLocaleChange(cur.value as Locale)}
          >
            {cur.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
